import { useMemo, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Modal, Tag, Tooltip, Typography } from 'antd';
import {
  UserAccountProfileResponse,
  useGetMyAccount,
  useGetOrganizationMemberships,
  useRemoveUserAccountFromOrganization,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { Row, Table } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const { Title, Text } = Typography;

export const MembersList = () => {
  const { activeOrganization } = useRouterContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>();

  const {
    data: membersData,
    isLoading: membersDataLoading,
    refetch: refetchMembers,
  } = useGetOrganizationMemberships(
    {
      pathParams: { id: activeOrganization?.id! },
    },
    {
      select: (data) => data?.admins,
      enabled: Boolean(activeOrganization?.id),
    },
  );
  const { data: currentUserData } = useGetMyAccount<{
    profile: UserAccountProfileResponse;
  }>({});
  const {
    mutate: removeOrganizationUser,
    isLoading: removeOrganizationUserLoading,
  } = useRemoveUserAccountFromOrganization();

  const columns = useMemo(
    () => [
      {
        title: 'First name',
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: 'Last name',
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record: any) =>
          record.id === currentUserData?.profile.id ? null : (
            <Button
              type="text"
              icon={
                <Tooltip title="Remove member">
                  <DeleteOutlined />
                </Tooltip>
              }
              onClick={() => {
                setSelectedMember(record);
                setModalOpen(true);
              }}
            />
          ),
      },
    ],
    [],
  );

  const handleOrganizationUserRemove = () => {
    try {
      removeOrganizationUser(
        {
          pathParams: {
            id: activeOrganization?.id!,
            memberId: selectedMember?.id!,
          },
        },
        {
          onSuccess: () => {
            setSelectedMember(undefined);
            refetchMembers();
            setModalOpen(false);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'members_on_error_remove_organization_user',
        action: 'remove_organization_user',
      });
    }
  };

  return (
    <>
      <Table
        columns={columns}
        dataSource={membersData}
        loading={membersDataLoading}
        rowKey={(record: any) => record.id}
      />
      <Modal
        title={
          <Title
            level={2}
            style={{ fontWeight: 300, lineHeight: 1, padding: '30px 0 0 0' }}
          >
            Remove member
          </Title>
        }
        open={modalOpen}
        footer={null}
        style={{ maxWidth: '90%', width: 450 }}
        onCancel={() => setModalOpen(false)}
      >
        <Text>
          Are you sure you want to remove member with this email:{' '}
          <strong>{selectedMember?.email}</strong> from{' '}
          <Tag style={{ fontWeight: 700, fontSize: 13 }}>
            {activeOrganization?.name}
          </Tag>{' '}
          organization? <br />
          <br />
          This member will no longer have access to{' '}
          <span style={{ fontWeight: 'bold' }}>
            {activeOrganization?.name}
          </span>{' '}
          organization.
        </Text>
        <Row justifyContent="flex-end" gap={15} style={{ marginTop: 40 }}>
          <Button size="large" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            danger
            loading={removeOrganizationUserLoading}
            onClick={handleOrganizationUserRemove}
          >
            Remove member
          </Button>
        </Row>
      </Modal>
    </>
  );
};
